<template>
    <f7-page
        class="new-payment-view"
        ref="self"
        @page:beforein="pageBeforeIn"
    >
        <div class="title-wrapper">
            <p class="title">{{ $t("newPayment.title") }}</p>
        </div>

        <view-payment-create-flow-tabs @update="updateFlowHandler" v-if="viewController.canUseExtWFlow()" :flow="flow"/>
        <f7-tabs>
            <ViewPaymentFlowClassicTab />
            <ViewPaymentFlowBromoneyTab v-if="viewController.canUseExtWFlow()" />
        </f7-tabs>
        <f7-block class="actions">
            <f7-button
                class="theme-type"
                preloader
                :disabled="isSubmitLoading || amount === 0"
                :loading="isSubmitLoading"
                @click="onSubmitPayment"
            >
                {{ t("newPayment.next") }}
            </f7-button>
            <view-payment-create-config-rules v-if="ViewRulesController.of().url.value" :actionText="actionText"/>
        </f7-block>
    </f7-page>
</template>

<script lang="ts" setup>
import NewPaymentController, { CreatePaymentFlow } from "./NewPaymentController";
import { computed, onMounted, Ref, ref, watch } from "vue";
import { f7 } from "framework7-vue";
import ErrorsService from "@/services/errors-service/ErrorsService";
import LightPaymentPidResponseError from "@models/responses/light-payment-pid/LightPaymentPidResponseError";
import ViewPaymentCreateAgentsController from "@/views/payment-create-agents/ViewPaymentCreateAgentsController";
import AgentsAmountRanges from "@models/errors/components/AgentsAmountRanges";
import {FirebaseService} from "@/services/firebase/FirebaseService";
import IFrameService from "@/services/iframe/IFrameService";
import {FirebaseEvents} from "@/services/firebase/analytics/FirebaseEvents";
import FirebaseEventBuilder from "@/services/firebase/analytics/FirebaseEventBuilder";
import {AccountError} from "@enums/ServerErrorCodes";
import {getDevice} from "framework7/shared/get-device";
import ViewPaymentCreateFlowTabs from "@/views/payment-create-config/comps/ViewPaymentCreateFlowTabs.vue";
import {useI18n} from "vue-i18n";
import ViewPaymentCreateConfigRules from "@/views/payment-create-config/comps/ViewPaymentCreateConfigRules.vue";
import ViewRulesController from "@/views/v2/rules/ViewRulesController";
import ViewPaymentFlowClassicTab from "@/views/payment-create-config/comps/ViewPaymentFlowClassicTab.vue";
import ViewPaymentFlowBromoneyTab from "@/views/payment-create-config/comps/ViewPaymentFlowBromoneyTab.vue";

const {t} = useI18n({useScope: 'global'});
const tabFlowMaxHeight = ref(0);
const self = ref(null);

const viewController = NewPaymentController.getInstance();
const viewPaymentCreateAgentsController = ViewPaymentCreateAgentsController.of();

const {
	// selectedPaymentSystem,
	amount,
	agentsAmountRanges,
	agent,
	flow,
	paymentService,
	routerService,
	inputAmountFocused,
	textMaxMinError,
	isSubmitLoading,
} = viewController;

const actionText = computed(() => {
	if (flow.value === CreatePaymentFlow.classic) {
		return t("newPayment.next")
	} else {
		return t("newPayment.from-bromoney-flow.next")
	}
});

const isLightAppType = computed(() => import.meta.env.VITE_APP_TYPE === 'light');

onMounted(async () => {
	await viewController.init();
	await fetchAgentsAmountRangesUseCase();
});

const fetchAgentsAmountRangesUseCase = async () => {
	const minMaxValues = await viewController.fetchAgentsAmountRanges();
	if (flow.value === CreatePaymentFlow.classic) {
        if (minMaxValues && minMaxValues.min) {
            checkAndUpdateAmountInputWithFloorAgentsAmount(amount, minMaxValues.min);
            return;
        }
        if (minMaxValues && minMaxValues.max) {
            checkAndUpdateAmountInputWithTopAgentsAmount(amount, minMaxValues.max);
        }
    }
}

const checkAndUpdateAmountInputWithFloorAgentsAmount = (amount: Ref<number>, minAgentAmount: number) => {
	if (amount.value < minAgentAmount) {
		amount.value = Math.ceil(minAgentAmount);
	}
}

const checkAndUpdateAmountInputWithTopAgentsAmount = (amount: Ref<number>, maxAgentAmount: number) => {
	if (amount.value > maxAgentAmount) {
		amount.value = Math.floor(maxAgentAmount);
	}
}

watch(agent, (newAgent) => {
	if (newAgent !== null) {
		viewController.setAgentsAmountRanges(new AgentsAmountRanges(newAgent.sumRanges.currency.min, newAgent.sumRanges.currency.max));

		if (agentsAmountRanges.value && agentsAmountRanges.value.min) {
			checkAndUpdateAmountInputWithFloorAgentsAmount(
				amount,
				agentsAmountRanges.value.min,
			);
		}
		if (agentsAmountRanges.value && agentsAmountRanges.value.max) {
			checkAndUpdateAmountInputWithTopAgentsAmount(
				amount,
				agentsAmountRanges.value.max,
			);
		}
	} else {
		viewController.fetchAgentsAmountRanges();
	}
});

// watch(selectedPaymentSystem, () => {
//     textMaxMinError.value = false;
// });

const closeKeyboardFocusInput = (callback: Function) => {
	if (getDevice().cordova && inputAmountFocused) {
		inputAmountFocused.value = false;
		setTimeout(async () => {
			await callback();
		}, 500);
		return false;
	}
	callback();
}

const pageBeforeIn = () => {
	setTimeout(() => {
		setHeightPopup();
	}, 0)
}

const setHeightPopup = () => {
	if (IFrameService.of().isIFrameView) return;

	const mainEl = (self.value! as any);
	let height = 0;
	const style = getComputedStyle(document.body);
	const children = mainEl.$el.querySelector('.page-content').children;

	for (let i = 0; i < children.length; i++) {
		const el = (children[i] as HTMLElement);
		if (el.classList.contains('tabs')) {
			tabFlowMaxHeight.value = Math.max(tabFlowMaxHeight.value, el.clientHeight);
			mainEl.$el.querySelector('.tabs').style.height = `${tabFlowMaxHeight.value}px`;
		}
		const styleEl = getComputedStyle(el);
		const rect = el.getBoundingClientRect()
		height += rect.height + parseFloat(styleEl['marginTop']) + parseFloat(styleEl['marginBottom']);
	}

	mainEl.$el.closest('.new-payment-popup').style.height = Math.ceil(height + parseInt(style.getPropertyValue('--f7-safe-area-bottom'))) + 'px';
}

const onSubmitPayment = async () => {
	closeKeyboardFocusInput(async () => {
		let isClosePopup = true;
		isSubmitLoading.value = true;

		try {
			if (!viewController.validateMinmax()) {
				isClosePopup = false;
				textMaxMinError.value = true;
			} else {
                if (flow.value === CreatePaymentFlow.fromBromoney) {
                    f7.views.main.router.navigate('/payment/confirm/wallet');
                    return
                } else {
                    await createPayment();
                    handleNavigationOnSuccess();
                }
			}
		} catch (e: any) {
			trackAnalytics(FirebaseEvents.PAYMENT_CREATING_ERROR);
			handlePaymentError(e, () => {
				isClosePopup = false
			});
		} finally {
			isSubmitLoading.value = false;
			if (isClosePopup) closePopup();
		}
	});
}

const createPayment = async () => {
	trackAnalytics(FirebaseEvents.PAYMENT_CREATING_TRY);
	paymentService.payment.value = await viewController.next();
	trackAnalytics(FirebaseEvents.PAYMENT_CREATING_SUCCESS);
}

const trackAnalytics = (event: FirebaseEvents) => {
	FirebaseService.of().analyticsEvent(
		event,
		FirebaseEventBuilder.of().build()
	);
};

const handleNavigationOnSuccess = () => {
	if (isLightAppType.value) {
		paymentService.paymentError.value = null;
	} else {
		f7.views.main.router.navigate('/payment');
	}
};

const handlePaymentError = (e: any, cancelClosePopup: () => void) => {
	const callback = getPaymentErrorHandler(e, cancelClosePopup);
	ErrorsService.of().handle(e, callback);
}

const getPaymentErrorHandler = (e: any, cancelClosePopup: () => void): (() => void | Promise<void>) | null => {
	switch (e.code) {
		case AccountError.AgentNotFound:
			return () => handleAgentNotFoundError(e);
		case AccountError.AgentsUnavailableForAmount:
			return () => handleAgentsUnavailableForAmountError(e, cancelClosePopup);
		case AccountError.AgentsUnavailableForRangeAmount:
			return () => handleAgentsUnavailableForRangeAmountError(e, cancelClosePopup);
		default:
			return null;
	}
}

const handleAgentNotFoundError = (e: any) => {
	if (isLightAppType.value) {
		paymentService.paymentError.value = e;
	} else {
		showNoAgentPopup();
	}
}

const handleAgentsUnavailableForAmountError = async (e: any, cancelClosePopup: () => void) => {
	const responseError = e.data;
	// @ts-ignore
	if (responseError.data.ranges!.max < responseError.data.ranges!.min) {
		if (isLightAppType.value) {
			paymentService.paymentError.value = e;
		} else {
			showNoAgentPopup();
		}
	} else {
		cancelClosePopup();
		agent.value = null;
		viewPaymentCreateAgentsController.paymentErrorWasShowed.value = false;
		paymentService.paymentError.value = new LightPaymentPidResponseError(e.code, null);
		await routerService.toViewPaymentCreateAgents();
	}
}

const handleAgentsUnavailableForRangeAmountError = async (e: any, cancelClosePopup: () => void) => {
	cancelClosePopup();
	viewPaymentCreateAgentsController.paymentErrorWasShowed.value = false;
	paymentService.paymentError.value = new LightPaymentPidResponseError(e.code, null);
	await routerService.toViewPaymentCreateAgents();
}

const showNoAgentPopup = () => {
	const el: HTMLDivElement = document.querySelector('.no-agents-sheet')!;
	const sheet = f7.sheet.create({
		el,
	});
	sheet.open();
}

const closePopup = () => {
	const popupEl: HTMLDivElement = document.querySelector('.new-payment-popup')!;
	f7.popup.get(popupEl).close();
}

const updateFlowHandler = (paymentFlow: CreatePaymentFlow) => {
	textMaxMinError.value = false;

	if (paymentFlow === CreatePaymentFlow.fromBromoney && viewController.canUseExtWFlow()) {
		flow.value = CreatePaymentFlow.fromBromoney;
	} else {
		flow.value = CreatePaymentFlow.classic;
	}

	amount.value = viewController.calculateStartAmountValue();
};
</script>

<style lang="scss">
.device-android, .device-ios {
  &.olimp, &.olimp-casino, &.olimpcasinokg {
    .new-payment-popup {
      //height: calc(var(--f7-safe-area-bottom) + 406px + 100px) !important;
      //height: calc(var(--f7-safe-area-bottom) + 406px) !important;
    }
  }
}

.new-payment-view {
  position: relative !important;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;

  &__warning {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 20px 16px 24px 16px;
  }

  .page-content {
    display: flex;
    flex-direction: column;

    .view-payment-create-flow-tabs {
      & + .tabs {
        margin-top: 0 !important;
      }
    }

    .tabs {
      margin-top: 24px;

      .tab {
        overflow: hidden;

        &#flow-from_bromoney {
          .hint {
            margin: 8px 16px 16px 16px;
            height: 12px;

            .text {
              @include text(12px, 400, var(--grey2));
            }
          }
        }

        .list {

        }
      }
    }
  }

  .title-wrapper {
    margin: 24px 16px 0;

    & + .tabs {
      margin-top: 0 !important;
    }

    .title {
      font-weight: 700;
      font-size: 24px;
      line-height: 26px;
      letter-spacing: 0.337647px;
      color: #000000;
    }
  }

  .default-amounts {
    height: auto;
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 0px;
    margin-top: 8px;
    gap: 8px;
  }

  .actions {
    margin-top: 24px;
    margin-bottom: 16px !important;
  }

  .list {
    margin-bottom: 0;

    & + .actions {
      margin-top: 40px;
    }
  }
}
</style>
